/**
 * OWA-998: what the API layer does with the answer to a task save (plan steps 9 and 14 to 17).
 *
 * - Accepted (written, unchanged, or already committed): the queue row is retired and the queued
 *   saves that continue it are rebased onto the accepted version, in one queue transaction, and
 *   only then is the success reported. When the transaction fails nothing is reported; the row is
 *   sent again and its retry is recognized as already committed.
 * - Refused as stale: nothing is written and no fatalError is set. A reconciling save (timer
 *   clean-up) is dropped; a save whose content the task already has is retired as accepted; any
 *   other save is held in the queue with the conflict for the user to resolve. The record as it is
 *   now is installed in the store over the refused version.
 */

import Task from '../entities/task';
import * as idb from '../lib/idbd';
import type {RequestSession} from '../Auth/sessionRequest';
import {isAssigneeLocked, isStaleConflict, sameContent, versionRef, type AssigneeLocked, type StaleConflict} from '../lib/staleSave';
import {markAccepted, markFailed, type QueuedTask, type SaveIntent} from '../lib/taskLineage';
import {saveRefused} from '../lib/refusedSaves';
import {staleTask} from '../store/actions/taskActions';

/** A task save's lineage, read before any hook can replace the object that carries it. */
export type SentTaskLineage = {opId?: string, saveIntent?: SaveIntent};

export const lineageOf = (item: unknown): SentTaskLineage => {
    const queued = item as QueuedTask;
    return {opId: queued?.opId, saveIntent: queued?.saveIntent};
};

/** The request body of a task save: pendingOp is this browser's own and is never sent. */
export function taskWire<T>(item: T): T {
    const json = typeof (item as {toJSON?: () => object})?.toJSON === 'function'
        ? (item as unknown as {toJSON: () => object}).toJSON()
        : {...(item as object)};
    // This browser's own fields are not sent: pendingOp, and the queue's operation id (OWA-1083)
    return {...json, pendingOp: undefined, opId: undefined} as T;
}

/** True when a 409 answer (or a batch slot's fatalError) is a stale refusal. */
export const staleConflictOf = (status: number, body: unknown): StaleConflict | null =>
    status === 409 && isStaleConflict(body) ? body : null;

/**
 * Retire an accepted task save and rebase the saves that continue it. False when the queue
 * transaction failed, in which case the caller must not report the save as a success.
 */
export async function acknowledgeTaskSave(session: RequestSession, sent: Task, lineage: SentTaskLineage, data: Task): Promise<boolean> {
    session.assertCurrent();
    const version = versionRef(data as Task & {addedOn: number});
    const result = await idb.acknowledgeTask(sent.addedOn, lineage.opId, version);
    session.assertCurrent();
    if (result.isErr()) {
        console.warn(`OWA-998: task ${sent.id} was saved but its queue row could not be retired yet; it will be sent again`, result.unwrapErr());
        return false;
    }
    markAccepted(lineage.opId, version);
    return true;
}

/** A task save the server refused as stale. */
export async function refuseTaskSave(session: RequestSession, dispatch: (action: unknown) => void,
                                     sent: Task, lineage: SentTaskLineage, conflict: StaleConflict): Promise<void> {
    session.assertCurrent();
    const current = conflict.current && typeof conflict.current === 'object'
        ? new Task({...(conflict.current as object), persisted: true})
        : null;

    if (lineage.saveIntent === 'reconcile') {
        // Timer clean-up: the next clean-up works from the record as it is now
        const retired = await idb.retireTasks([sent.addedOn]);
        if (retired.isErr()) console.warn(retired.unwrapErr());
        else markFailed(lineage.opId);
    }
    else if (current && sameContent(sent, current)) {
        // Nothing to save: the task already has this content (another tab made the same change,
        // or the save's own answer was lost after the clock was clamped)
        const retired = await idb.acknowledgeTask(sent.addedOn, lineage.opId, versionRef(current as Task & {addedOn: number}));
        if (retired.isErr()) console.warn(retired.unwrapErr());
        else markAccepted(lineage.opId, versionRef(current as Task & {addedOn: number}));
    }
    else {
        const held = await idb.holdTask(sent.addedOn, conflict);
        if (held.isErr()) console.warn(`OWA-998: could not hold refused task save ${sent.id}; it will be sent again`, held.unwrapErr());
    }
    session.assertCurrent();

    if (!current) return;
    // The store may show the refused save or a later save that continues it (it waits behind the
    // refused one): show the task as it is now instead of either
    const replaced = [sent.addedOn, ...await continuationsOf(lineage.opId)];
    session.assertCurrent();
    for (const addedOn of replaced) dispatch(staleTask(current, addedOn));
}

/** An entity's revert action creator (EntityMapper.revertSave), for the task entity. */
export type RevertSave = (task: Task, options: {sentAddedOn?: number, current?: Task | null}) => unknown;

/**
 * True when a 409 answer (or a batch slot's fatalError) refuses a reassignment of a task whose
 * timer has started (OWA-842). A batch slot may carry no status of its own.
 */
export const assigneeLockedOf = (status: number, body: unknown): AssigneeLocked | null =>
    (status === 409 || Number.isNaN(status)) && isAssigneeLocked(body) ? body : null;

/**
 * OWA-842: a task save refused because the assignee cannot change after the timer has started.
 * The refusal is permanent: the queued save is dropped (never retried and not kept for repair),
 * its pending row returns to the task as the server holds it, and the refusal is recorded for a
 * notice to explain. `revertSave` is the task entity's revertTaskSave action creator.
 */
export async function refuseLockedAssignee(session: RequestSession, dispatch: (action: unknown) => void, revertSave: RevertSave,
                                           sent: Task, lineage: SentTaskLineage, locked: AssigneeLocked): Promise<void> {
    session.assertCurrent();
    const retired = await idb.retireTasks([sent.addedOn]);
    session.assertCurrent();
    if (retired.isErr()) console.warn(`OWA-842: could not drop refused task save ${sent.id}; it will be refused again`, retired.unwrapErr());
    else markFailed(lineage.opId);

    const current = locked.current && typeof locked.current === 'object'
        ? new Task({...(locked.current as object), persisted: true})
        : null;
    dispatch(revertSave(sent, {sentAddedOn: sent.addedOn, current}));
    dispatch(saveRefused({entity: 'task', id: sent.id, code: locked.code, message: locked.message, at: Date.now()}));
}

/** The queue keys of the queued saves that continue `opId`, directly or through another. */
async function continuationsOf(opId: string | undefined): Promise<number[]> {
    if (!opId) return [];
    const rows = await idb.getSingleStore('task');
    if (rows.isErr()) return [];
    const queued = rows.unwrap() as QueuedTask[];
    const found: number[] = [];
    const visit = (parent: string) => queued.filter(row => row.parentOp === parent).forEach(row => {
        found.push(row.addedOn);
        if (row.opId) visit(row.opId);
    });
    visit(opId);
    return found;
}
